// Juspay HyperCheckout (Cashfree behind it) for WEBSITE WALLET TOP-UPS.
//
// Replaces PayU in the "try this first, fall back to Razorpay" slot. The backend
// lives in the Go service (internal/payments/juspay*.go); this file is the
// browser half.
//
// WHY AN IFRAME. Juspay has no JavaScript SDK for HyperCheckout on the web. Its
// documented in-page integration is the hosted payment page in an iframe with
// allow="payment *" (needed for UPI intents and Apple Pay), which keeps the user
// on astroaura.ai the way PayU's Bolt popup did. Below 700px wide Juspay switches
// to its own mobile layout, so one overlay serves phones and desktops. The one
// exception is a foreign order on an Apple Pay browser: see opensFullPage.
//
// WHO DECIDES. Never this file. When the user finishes, Juspay navigates the
// iframe to /juspay-return.html, which posts the order id back here. This file
// then asks juspay-order-status, the backend asks Juspay, and settles the wallet.
// The iframe's own URL parameters (status=, signature=) are never read. Closing
// the window early is handled the same way: we still ask, because people pay in
// a UPI app and close the checkout before it has caught up.
//
// THE PRICE IS THE SERVER'S. The page sends the pack id and whether its screen
// showed GST; the backend prices the pack from wallet_packages. No amount ever
// leaves the browser.

import { supabase } from "../supabaseClient";
import { AnalyticsManager, flushAnalytics, trackEvent } from "./analytics";
import type { PurchaseItem } from "./analytics";

/** What the page should tell the user. */
export type JuspayResult =
  /** The wallet is credited (now, or it already was). */
  | "credited"
  /** Paid, or possibly paid, and not settled yet. The webhook and the sweep
   *  will credit it; tell the user it is on its way, never that it failed. */
  | "processing"
  /** The payment did not go through. */
  | "failed"
  /** Closed before paying. Say nothing. */
  | "cancelled"
  /** Finished in Juspay's page (its Cancel, or backing out) with no payment
   *  ever started on the order: Juspay still says NEW. Nothing was charged. */
  | "not_paid";

/** Juspay is not available for this top-up (switched off, currency not open
 *  yet, could not start). The caller falls back to Razorpay, exactly as it did
 *  when PayU answered {skip:true}. Only ever thrown BEFORE the checkout opens. */
export class JuspaySkip extends Error {
  constructor(public reason: string) {
    super(reason);
    this.name = "JuspaySkip";
  }
}

export interface JuspayTopupArgs {
  packageId: string;
  /** The currency the screen showed. The backend refuses to charge another. */
  currency: string;
  /** True only when the screen showed price + 18% GST (the wallet page's INR
   *  summary). The backend computes that total itself. */
  withGst: boolean;
}

export interface JuspayTopupOutcome {
  result: JuspayResult;
  orderId: string;
  /** What Juspay charged, in minor units, as priced by the server. */
  amountMinor: number;
  creditMinor: number;
  currency: string;
}

const ORDER_ID_RE = /^[a-z0-9]{1,21}$/;
const RETURN_SOURCE = "aura-juspay-return";

// ---------------------------------------------------------------------------
// Server calls
// ---------------------------------------------------------------------------

interface Session {
  orderId: string;
  paymentUrl: string;
  amountMinor: number;
  creditMinor: number;
  currency: string;
}

async function createSession(args: JuspayTopupArgs): Promise<Session> {
  let data: any;
  try {
    const res = await supabase.functions.invoke("create-juspay-session", {
      body: {
        purpose: "wallet_topup",
        package_id: args.packageId,
        with_gst: args.withGst,
        currency: (args.currency || "").toUpperCase(),
        return_origin: window.location.origin,
      },
    });
    if (res.error) throw new JuspaySkip(res.error.message || "juspay_init_failed");
    data = res.data;
  } catch (e) {
    if (e instanceof JuspaySkip) throw e;
    throw new JuspaySkip((e as Error)?.message || "juspay_init_failed");
  }
  if (!data || data.skip) throw new JuspaySkip(data?.reason || "juspay_skip");
  if (
    typeof data.payment_url !== "string" ||
    !/^https:\/\//.test(data.payment_url) ||
    !ORDER_ID_RE.test(String(data.order_id || ""))
  ) {
    throw new JuspaySkip("juspay_bad_response");
  }
  return {
    orderId: data.order_id,
    paymentUrl: data.payment_url,
    amountMinor: Number(data.amount_minor) || 0,
    creditMinor: Number(data.credit_minor) || 0,
    currency: String(data.currency || args.currency || "").toUpperCase(),
  };
}

/** Ask the backend where an order stands. It asks Juspay and settles. Any
 *  failure to get an answer reads as "processing": not knowing is not failing. */
export async function checkJuspayOrder(
  orderId: string,
): Promise<{ outcome: string; status: string }> {
  if (!ORDER_ID_RE.test(orderId)) return { outcome: "unknown", status: "" };
  try {
    const { data, error } = await supabase.functions.invoke("juspay-order-status", {
      body: { order_id: orderId },
    });
    if (error || !data) return { outcome: "processing", status: "UNKNOWN" };
    return { outcome: String(data.outcome || "processing"), status: String(data.status || "") };
  } catch {
    return { outcome: "processing", status: "UNKNOWN" };
  }
}

/**
 * Turn the backend's answer into what the page says.
 *
 * Only an explicit "failed" is shown as a failure. review / mismatch / error /
 * unknown all mean money MAY have moved, so they read as "processing" and the
 * sweep, or a person, finishes them. Telling someone who paid that it failed is
 * how they pay twice.
 */
export function juspayResultFor(outcome: string, status: string, finished: boolean): JuspayResult {
  if (outcome === "credited" || outcome === "already") return "credited";
  if (outcome === "failed") return "failed";
  // Closed with no payment attempt at all: nothing happened, say nothing.
  if (!finished && outcome === "processing" && status === "NEW") return "cancelled";
  return "processing";
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// A finished checkout that reads NEW this many times in a row (the first two
// checks, ~2s apart) never had a payment started on it. Juspay records a
// transaction the moment a method is chosen, before any money moves, so a
// cancel in its page or a back-out lands here, while a UPI intent that was
// opened reads AUTHORIZING and stays "processing".
const NOT_PAID_AFTER_NEW = 2;

/**
 * Check, then keep checking for a short while if it is still settling (a UPI
 * payment can take a few seconds to confirm). Stops at the first definite
 * answer.
 *
 * With `onInterim`, a "not_paid" is reported early but checking carries on for
 * the whole window, so a page can show its way out at once and still switch
 * to success if a payment lands after all. Without it, "not_paid" is returned
 * as soon as it is known. `isCancelled` stops the checks when a page unmounts.
 */
export async function settleJuspayOrder(
  orderId: string,
  finished: boolean,
  opts: { onInterim?: (r: JuspayResult) => void; isCancelled?: () => boolean } = {},
): Promise<JuspayResult> {
  let last: JuspayResult = "processing";
  let newInARow = 0;
  // ~20s in total: Juspay's own guidance polls every 15s for the first 90s,
  // and the webhook plus the 10-minute sweep cover anything slower.
  const delays = [0, 2000, 3000, 4000, 5000, 6000];
  for (const d of delays) {
    if (d) await sleep(d);
    if (opts.isCancelled?.()) return last;
    const { outcome, status } = await checkJuspayOrder(orderId);
    last = juspayResultFor(outcome, status, finished);
    if (last !== "processing") return last;
    // Closed before paying and Juspay never saw an attempt: no point waiting.
    if (!finished && status === "NEW") return "cancelled";
    newInARow = status === "NEW" ? newInARow + 1 : 0;
    if (finished && newInARow === NOT_PAID_AFTER_NEW) {
      if (!opts.onInterim) return "not_paid";
      opts.onInterim("not_paid");
    }
  }
  return finished && newInARow >= NOT_PAID_AFTER_NEW ? "not_paid" : last;
}

const settle = (orderId: string, finished: boolean) => settleJuspayOrder(orderId, finished);

// ---------------------------------------------------------------------------
// Purchase tracking that survives a late confirmation
// ---------------------------------------------------------------------------
//
// A UPI payment often confirms after settle() stops asking. The webhook or the
// sweep then credits it, but the page has moved on, so the Purchase event
// (Pixel, GA4, Amplitude, CleverTap) never fired and Juspay under-counted
// against Razorpay. So the purchase is written down when the checkout opens,
// and fired exactly once by whichever path first sees the order credited: the
// page itself, a re-check while the tab is open, the next visit, or the
// payment-success page. No stored entry means no event: a missing purchase is
// better than one with a guessed value.

/** What the caller logs for this top-up: the same values as its credited path. */
export interface JuspayPurchase {
  userId: string;
  value: number;
  items?: PurchaseItem[];
}

interface PendingPurchase extends JuspayPurchase {
  orderId: string;
  currency: string;
  savedAt: number;
}

const PENDING_KEY = "aura_juspay_pending_purchases";
const LOGGED_KEY = "aura_juspay_logged_purchases";
const PENDING_MAX = 5;
const PENDING_TTL_MS = 48 * 60 * 60 * 1000;
const LOGGED_MAX = 50;
const RECHECK_AFTER_MS = [30_000, 120_000];
const FLUSH_WAIT_MS = 400;

function readList<T>(key: string): T[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: unknown[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* storage blocked: only the in-page credited path can log */
  }
}

const livePending = (now = Date.now()) =>
  readList<PendingPurchase>(PENDING_KEY).filter(
    (p) => p && ORDER_ID_RE.test(String(p.orderId)) && now - Number(p.savedAt) < PENDING_TTL_MS,
  );

function rememberPurchase(entry: PendingPurchase) {
  const rest = livePending().filter((p) => p.orderId !== entry.orderId);
  writeList(PENDING_KEY, [...rest, entry].slice(-PENDING_MAX));
}

function forgetPurchase(orderId: string) {
  writeList(PENDING_KEY, livePending().filter((p) => p.orderId !== orderId));
}

// Orders logged by this tab, for when storage is blocked.
const loggedHere = new Set<string>();

/**
 * Fire the stored purchase for this order, at most once per browser. The entry
 * is removed BEFORE the event goes out, so two paths racing cannot both fire.
 * Returns whether an event was sent.
 */
export function logJuspayPurchaseOnce(orderId: string, late: boolean): boolean {
  const entry = livePending().find((p) => p.orderId === orderId);
  forgetPurchase(orderId);
  const logged = readList<string>(LOGGED_KEY);
  if (!entry || loggedHere.has(orderId) || logged.includes(orderId)) return false;
  loggedHere.add(orderId);
  writeList(LOGGED_KEY, [...logged, orderId].slice(-LOGGED_MAX));
  AnalyticsManager.logPurchase(orderId, entry.value, entry.currency, entry.items, {
    gateway: "juspay",
    late,
  });
  return true;
}

/** True once this order's Purchase has gone out, from any path. */
export function isJuspayPurchaseLogged(orderId: string): boolean {
  return loggedHere.has(orderId) || readList<string>(LOGGED_KEY).includes(orderId);
}

/** Ask about one stored order: fire on credited, drop on failed, else keep. */
async function recheckPurchase(orderId: string): Promise<void> {
  if (!livePending().some((p) => p.orderId === orderId)) return;
  const { outcome, status } = await checkJuspayOrder(orderId);
  const result = juspayResultFor(outcome, status, true);
  if (result === "credited") logJuspayPurchaseOnce(orderId, true);
  else if (result === "failed") forgetPurchase(orderId);
}

let reconciling: Promise<void> | null = null;

/**
 * Re-check this user's stored Juspay orders. Run on app load and when the tab
 * comes back into view. Cheap when there is nothing stored, and safe to repeat:
 * the server settles an order once and answers "already" after that.
 */
export function reconcileJuspayPurchases(userId: string): Promise<void> {
  if (!userId) return Promise.resolve();
  if (reconciling) return reconciling;
  const live = livePending();
  writeList(PENDING_KEY, live); // drops expired entries
  const mine = live.filter((p) => p.userId === userId);
  if (!mine.length) return Promise.resolve();
  reconciling = (async () => {
    for (const p of mine) await recheckPurchase(p.orderId).catch(() => {});
  })().finally(() => {
    reconciling = null;
  });
  return reconciling;
}

// ---------------------------------------------------------------------------
// The overlay
// ---------------------------------------------------------------------------

interface Overlay {
  /** Resolves "finished" when /juspay-return.html reports this order, or
   *  "closed" when the user closes the window. */
  done: Promise<"finished" | "closed">;
  remove: () => void;
}

function openOverlay(paymentUrl: string, orderId: string): Overlay {
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  const backdrop = document.createElement("div");
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-modal", "true");
  backdrop.setAttribute("aria-label", "Secure payment");
  // Top of everything: the recharge sheet and the wallet summary are modals
  // with their own z-index, and PayU's popup once rendered behind them.
  Object.assign(backdrop.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    background: "rgba(10, 8, 20, 0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  } as CSSStyleDeclaration);

  const panel = document.createElement("div");
  const narrow = window.innerWidth < 700;
  Object.assign(panel.style, {
    position: "relative",
    width: narrow ? "100vw" : "min(1000px, 96vw)",
    height: narrow ? "100%" : "min(920px, 94vh)",
    background: "#ffffff",
    borderRadius: narrow ? "0" : "14px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
  } as CSSStyleDeclaration);

  const bar = document.createElement("div");
  Object.assign(bar.style, {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px 10px 16px",
    borderBottom: "1px solid #eee",
    font: "600 14px/1.2 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1f1f2e",
  } as CSSStyleDeclaration);
  const label = document.createElement("span");
  label.textContent = "Secure payment";
  const close = document.createElement("button");
  close.type = "button";
  close.setAttribute("aria-label", "Close payment");
  close.textContent = "×";
  Object.assign(close.style, {
    border: "0",
    background: "transparent",
    fontSize: "26px",
    lineHeight: "1",
    width: "40px",
    height: "40px",
    cursor: "pointer",
    color: "#555",
    borderRadius: "8px",
  } as CSSStyleDeclaration);
  bar.append(label, close);

  const frame = document.createElement("iframe");
  frame.src = paymentUrl;
  frame.title = "Payment";
  // Without this, UPI intent hand-off and Apple Pay are blocked in the frame.
  frame.setAttribute("allow", "payment *");
  Object.assign(frame.style, {
    flex: "1 1 auto",
    width: "100%",
    border: "0",
    background: "#fff",
  } as CSSStyleDeclaration);

  panel.append(bar, frame);
  backdrop.append(panel);
  document.body.append(backdrop);

  let settleDone: (v: "finished" | "closed") => void = () => {};
  const done = new Promise<"finished" | "closed">((resolve) => {
    settleDone = resolve;
  });

  const onMessage = (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;
    const d = e.data as any;
    if (!d || d.source !== RETURN_SOURCE) return;
    // An empty id still means "the payment page is finished" for the order
    // that is open; a DIFFERENT id is some other tab's order and is ignored.
    if (d.order_id && d.order_id !== orderId) return;
    settleDone("finished");
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") settleDone("closed");
  };
  close.addEventListener("click", () => settleDone("closed"));
  window.addEventListener("message", onMessage);
  window.addEventListener("keydown", onKey);

  const remove = () => {
    window.removeEventListener("message", onMessage);
    window.removeEventListener("keydown", onKey);
    backdrop.remove();
    document.body.style.overflow = prevOverflow;
  };
  return { done, remove };
}

// ---------------------------------------------------------------------------
// Full page, for Apple Pay
// ---------------------------------------------------------------------------

// TEMPORARY, for the Apple Pay test: these accounts get full page on INR too, so
// they can try Apple Pay in Safari without switching wallet currency. Arjun,
// Vikram's phone login, Vikram's Dubai login. The server hides Apple Pay from
// everyone else. Remove once the test is done.
const APPLE_PAY_TEST_USER_IDS = [
  "d96278fd-b827-470e-8a7a-03e75fffc4ae",
  "fd894186-1cf0-46c7-accd-b765cc6aba4a",
  "dd05e6b1-0300-4379-bc15-cf1e625b80e4",
];

/**
 * Foreign orders on an Apple-Pay-capable browser open Juspay's page full page
 * instead of in the overlay. In an iframe on our page, Apple would want
 * astroaura.ai verified for Juspay, and our one /.well-known slot holds
 * Razorpay's file (live, do not touch). Full page, Apple Pay runs on Juspay's
 * own domain. Same test as payuBolt.ts: ApplePaySession only exists where the
 * button could appear, so everyone else keeps the overlay. INR stays in the
 * overlay, apart from APPLE_PAY_TEST_USER_IDS. Only testers get non-INR Juspay
 * orders while the rollout switch says currencies=INR.
 */
function opensFullPage(currency: string, userId?: string): boolean {
  if (currency === "INR" && !APPLE_PAY_TEST_USER_IDS.includes(userId ?? "")) return false;
  try {
    return !!(window as any).ApplePaySession?.canMakePayments?.();
  } catch {
    return false;
  }
}

/**
 * Leave for Juspay's page. Resolves only if the browser later restores this
 * very page from its back-forward cache: Safari brings it back frozen mid-await,
 * and without this the top-up button would stay busy for good. A Back that
 * reloads instead starts the app afresh, where the button works and
 * reconcileJuspayPurchases picks the order up.
 */
async function leaveForPaymentPage(paymentUrl: string): Promise<void> {
  // Amplitude flushes every 10s, too slow for an event fired just before
  // leaving. Capped, so a slow network cannot hold up the checkout.
  await Promise.race([flushAnalytics().catch(() => {}), sleep(FLUSH_WAIT_MS)]);
  await new Promise<void>((resolve) => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      window.removeEventListener("pageshow", onShow);
      resolve();
    };
    window.addEventListener("pageshow", onShow);
    window.location.assign(paymentUrl);
  });
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Run a wallet top-up through Juspay.
 *
 * Throws JuspaySkip if Juspay is not available for this top-up; the caller then
 * falls back to Razorpay. Once the checkout window has opened it never throws:
 * it always resolves with what to tell the user.
 *
 * `onOpened` fires once the checkout is on screen, so a caller can close its
 * own modal then and not before (on a skip, that modal stays for Razorpay).
 *
 * `purchase` is what the caller would log on success. This function logs it
 * (exactly once, now or when the order confirms later), so callers must not
 * call logPurchase themselves for a Juspay top-up.
 */
export async function runJuspayTopup(
  args: JuspayTopupArgs,
  hooks: { onOpened?: () => void; purchase?: JuspayPurchase } = {},
): Promise<JuspayTopupOutcome> {
  const session = await createSession(args);
  // Written down before the checkout opens, so it survives a full-page trip:
  // /payment-success and reconcileJuspayPurchases read it back on return.
  if (hooks.purchase) {
    rememberPurchase({
      ...hooks.purchase,
      orderId: session.orderId,
      currency: session.currency,
      savedAt: Date.now(),
    });
  }

  const fullPage = opensFullPage(session.currency, hooks.purchase?.userId);
  trackEvent("Juspay Checkout Opened", {
    order_id: session.orderId,
    currency: session.currency,
    amount_minor: session.amountMinor,
    credit_minor: session.creditMinor,
    with_gst: args.withGst,
    full_page: fullPage,
  });
  const notifyOpened = () => {
    try {
      hooks.onOpened?.();
    } catch {
      /* a caller's UI hook must never strand the checkout */
    }
  };

  let how: "finished" | "closed" | "back";
  if (fullPage) {
    notifyOpened();
    await leaveForPaymentPage(session.paymentUrl);
    how = "back"; // only reached when the browser restored this page
  } else {
    const overlay = openOverlay(session.paymentUrl, session.orderId);
    notifyOpened();
    how = await overlay.done;
    overlay.remove();
  }
  const result = await settle(session.orderId, how === "finished");

  if (result === "credited") {
    logJuspayPurchaseOnce(session.orderId, false);
  } else if (result === "failed" || result === "cancelled") {
    forgetPurchase(session.orderId);
  } else {
    // Still confirming, or not paid as far as Juspay knows: look again while
    // the tab is open. The next visit (reconcileJuspayPurchases) covers anyone
    // who has left by then, and an order that never pays simply ages out.
    for (const ms of RECHECK_AFTER_MS) {
      setTimeout(() => {
        recheckPurchase(session.orderId).catch(() => {});
      }, ms);
    }
  }

  trackEvent("Juspay Checkout Result", {
    order_id: session.orderId,
    result,
    ended_by: how,
    currency: session.currency,
    amount_minor: session.amountMinor,
  });
  return {
    result,
    orderId: session.orderId,
    amountMinor: session.amountMinor,
    creditMinor: session.creditMinor,
    currency: session.currency,
  };
}
